import { Fragment, useEffect, useState } from "react";
import { Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import { get, postForm } from "../../../../utils/helpers/api";
import { STORAGE_URL } from "../../../../utils/constants";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import WarehouseSelect from "../../../../components/Forms/WarehouseSelect";
import { useBranch } from "../../../../context/branch";

function CreateProductPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { branchWarehouses } = useBranch();
  const [saveLoading, setSaveLoading] = useState(false);
  const [form, setForm] = useState({
    type: state?.productData?.type ?? "finished",
    category_id: state?.productData?.category_id ?? "",
    name: state?.productData?.name ?? "",
    description: state?.productData?.description ?? "",
    sku: state?.productData?.sku ?? "",
    barcode: state?.productData?.barcode ?? "",
    cost_price: state?.productData?.cost_price ?? "",
    selling_price: state?.productData?.selling_price ?? "",
    current_stock: state?.productData?.current_stock ?? "",
    minimum_stock: state?.productData?.minimum_stock ?? "",
    // Only meaningful on create: it says where the opening stock is recorded.
    warehouse_id: "",
    gross_weight: state?.productData?.gross_weight ?? "",
    net_weight: state?.productData?.net_weight ?? "",
    unit_weight: state?.productData?.unit_weight ?? "g",
    product_length: state?.productData?.product_length ?? "",
    product_width: state?.productData?.product_width ?? "",
    product_height: state?.productData?.product_height ?? "",
    volume: state?.productData?.volume ?? 0,
    unit_of_measure: state?.productData?.unit_of_measure ?? "cm"
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    initData();
  }, [id]);

  /**
   * Preselect the branch's default warehouse so the usual single-warehouse case
   * needs no extra click. Omitting it would land on the same warehouse anyway —
   * the field is there for companies that hold stock in more than one.
   */
  useEffect(() => {
    if (id || form.warehouse_id || !branchWarehouses.length) return;
    const preferred = branchWarehouses.find(w => w.is_default && w.type !== 'in_transit')
      ?? branchWarehouses.find(w => w.type !== 'in_transit');
    if (preferred) setForm(prev => ({ ...prev, warehouse_id: preferred.id }));
  }, [id, branchWarehouses, form.warehouse_id]);

  const initData = async () => {
    const data = await get("/inventory/products/create");
    if (data?.success) {
       setCategories(data.categories.data ?? data.categories);
      if (id && state?.productData?.image) {
        setImagePreview(`${STORAGE_URL}/${state.productData.image}`);
      }
    }
  };

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  /**
   * On edit, the stock fields are left out entirely: on-hand quantity belongs to
   * the warehouse levels and the API ignores it here, so posting it back would
   * only suggest the form can change it.
   */
  const buildFormData = () => {
    const skip = id ? ["current_stock", "warehouse_id"] : [];
    const fd = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (skip.includes(key)) return;
      if (val !== "" && val !== null && val !== undefined) fd.append(key, val);
    });
    if (imageFile) fd.append("image", imageFile);
    return fd;
  };

  const submit = async () => {
    setSaveLoading(true);
    const token = localStorage.getItem("access_token");
    const url = id
      ? `/inventory/products/update/${id}`
      : `/inventory/products/store`;

    try {
      const data = await postForm(url, buildFormData());
      if (data?.success) {
        navigate("/inventory/products");
      } else {
        toast.error(data?.message || t('common.somethingWentWrong'));
      }
    } catch {
      toast.error(t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const calculateVolume = () => {
    const { product_length, product_width, product_height } = form;
    if (product_length && product_width && product_height) {
      const volume = parseFloat(product_length) * parseFloat(product_width) * parseFloat(product_height);
      setForm(prev => ({ ...prev, volume: volume.toFixed(2) }));
    } else {
      setForm(prev => ({ ...prev, volume: 0 }));
    }
  };

  useEffect(() => {
    calculateVolume();
  }, [form.product_length, form.product_width, form.product_height]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('inventory.products'), id ? t('inventory.editProduct') : t('inventory.newProduct')]}
          onBack={() => navigate("/inventory/products")}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="6">
              <Label>{t('common.type')}: <span className="text-danger">*</span></Label>
              <Input type="select" name="type" value={form.type} onChange={onChange}>
                <option value="raw-material">{t('inventory.rawMaterials')}</option>
                <option value="finished">{t('inventory.finishedGoods')}</option>
                <option value="semi-finished">{t('inventory.semiFinishedGoods')}</option>
                <option value="trading">{t('inventory.tradingGoods')}</option>
                <option value="packaging">{t('inventory.packagingMaterials')}</option>
                <option value="non-stock">{t('inventory.nonStockItems')}</option>
                <option value="tools">{t('inventory.tools')}</option>
                <option value="service">{t('inventory.service')}</option>
              </Input>
            </Col>
            <Col md="6">
              <Label>{t('inventory.category')}:</Label>
              <Select
                options={categories.map(c => ({ value: c.id, label: c.name }))}
                styles={SelectCustomStyles}
                value={categories.map(c => ({ value: c.id, label: c.name })).find(o => o.value === form.category_id) || null}
                onChange={selected => setForm({ ...form, category_id: selected ? selected.value : null })}
              />
            </Col>
          </Row>

          <FormGroup>
            <Label>{t('common.name')}: <span className="text-danger">*</span></Label>
            <Input name="name" value={form.name} onChange={onChange} />
          </FormGroup>

          <Row className="g-3 mb-3">
            <Col md="6">
              <Label>{t('inventory.sellingPrice')}</Label>
              <InputGroup>
                <InputGroupText>{ settings?.sales?.base_currency }</InputGroupText>
                <Input 
                  type="number" 
                  name="selling_price" 
                  value={form.selling_price} 
                  onChange={onChange} 
                  min="0" 
                  step="0.01" 
                />
              </InputGroup>
            </Col>

            {form.type !== "service" &&
              <Fragment>
                <Col md="6">
                  <Label>{t('inventory.costPrice')}</Label>
                  <InputGroup>
                    <InputGroupText>{ settings?.sales?.base_currency }</InputGroupText>
                    <Input
                      name="cost_price"
                      type="number"
                      value={form.cost_price}
                      onChange={onChange}
                      min="0"
                      step="0.01"
                    />
                  </InputGroup>
                </Col>
                
                <Col md="6">
                  <Label>{t('inventory.sku')}</Label>
                  <Input name="sku" value={form.sku} onChange={onChange} />
                </Col>
                <Col md="6">
                  <Label>{t('inventory.barcode')}</Label>
                  <Input name="barcode" value={form.barcode} onChange={onChange} />
                </Col>

                <Col md="6">
                  <Label>{t('inventory.currentStock')}</Label>
                  <Input
                    type="number"
                    name="current_stock"
                    value={form.current_stock}
                    onChange={onChange}
                    min="0"
                    step="any"
                    disabled={!!id}
                  />
                  {id && (
                    <small className="text-muted d-block mt-1">
                      {t('inventory.stockEditedByAdjustment')}
                    </small>
                  )}
                </Col>
                <Col md="6">
                  <Label>{t('inventory.minimumStock')}</Label>
                  <Input type="number" name="minimum_stock" value={form.minimum_stock} onChange={onChange} min="0" step="any" />
                </Col>

                {/* Opening stock has to land in a warehouse: the product total is
                    only the sum of the per-warehouse levels. */}
                {!id && Number(form.current_stock) > 0 && (
                  <Col md="6">
                    <Label>{t('inventory.stockGoesToWarehouse')}</Label>
                    <WarehouseSelect
                      value={form.warehouse_id}
                      onChange={(value) => setForm(prev => ({ ...prev, warehouse_id: value }))}
                    />
                    <small className="text-muted d-block mt-1">
                      {t('inventory.stockGoesToWarehouseHint')}
                    </small>
                  </Col>
                )}
              </Fragment>
            }
          </Row>

          {form.type !== 'service' &&
            <Fragment>
              <h5>{t('inventory.weight')}</h5>
              <Row className="mb-3 g-3">
                <Col md="5">
                  <Label>{t('inventory.grossWeight')}</Label>
                  <Input name="gross_weight" value={form.gross_weight} onChange={onChange} />
                </Col>
                <Col md="5">
                  <Label>{t('inventory.netWeight')}</Label>
                  <Input name="net_weight" value={form.net_weight} onChange={onChange} />
                </Col>
                <Col md="2">
                  <Label>{t('inventory.unit')}</Label>
                  <Input type="select" name="unit_weight" value={form.unit_weight} onChange={onChange}>
                    <option value="">{t('common.select')}..</option>
                    <option value="mg">{t('inventory.unitMg')}</option>
                    <option value="g">{t('inventory.unitG')}</option>
                    <option value="kg">{t('inventory.unitKg')}</option>
                    <option value="t">{t('inventory.unitTonnes')}</option>
                  </Input>
                </Col>
              </Row>

              <h5>{t('inventory.dimensions')}</h5>
              <Row className="mb-3 g-3">
                <Col md="2">
                  <Label>{t('inventory.length')}</Label>
                  <Input name="product_length" value={form.product_length} onChange={onChange} />
                </Col>
                <Col md="2">
                  <Label>{t('inventory.width')}</Label>
                  <Input name="product_width" value={form.product_width} onChange={onChange} />
                </Col>
                <Col md="2">
                  <Label>{t('inventory.height')}</Label>
                  <Input name="product_height" value={form.product_height} onChange={onChange} />
                </Col>
                <Col md="3">
                  <Label>{t('inventory.volume')}</Label>
                  <Input name="volume" readOnly value={form.volume} />
                </Col>
                <Col md="3">
                  <Label>{t('inventory.unit')}</Label>
                  <Input type="select" name="unit_of_measure" value={form.unit_of_measure} onChange={onChange}>
                    <option value="">{t('common.select')}..</option>
                    <option value="mm">mm³</option>
                    <option value="cm">cm³</option>
                    <option value="m">m³</option>
                  </Input>
                </Col>
              </Row>
            </Fragment>
          }

          <FormGroup>
            <Label>{t('common.description')}:</Label>
            <Input type="textarea" name="description" value={form.description} onChange={onChange} rows="4" />
          </FormGroup>

          <FormGroup>
            <Label>{t('inventory.image')}</Label>
            <Input type="file" accept="image/*" onChange={onImageChange} />
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt={t('inventory.preview')}
                  style={{ maxHeight: 140, maxWidth: 200, borderRadius: 6, border: "1px solid #dee2e6" }}
                />
              </div>
            )}
          </FormGroup>
        </div>
      </div>

      <FixedSave 
        onSave={submit} 
        loading={saveLoading} 
        disabled={saveLoading} 
      />
    </SidebarLayout>
  );
}

export default CreateProductPage;
